import { isOrgUnfunded } from '@developerz/domain';
import { useSearchParams } from '@solidjs/router';
import { createMemo, createSignal, type JSX, Show } from 'solid-js';
import { AiAccountsPanel } from '../components/AiAccountsPanel';
import { ConnectMachineModal } from '../components/ConnectMachineModal';
import { EmptyState } from '../components/EmptyState';
import { FleetAttention } from '../components/FleetAttention';
import { FleetFirstRun } from '../components/FleetFirstRun';
import { FleetHeader } from '../components/FleetHeader';
import { FleetHealthTiles } from '../components/FleetHealthTiles';
import { FleetLive } from '../components/FleetLive';
import { FleetMachinesSection } from '../components/FleetMachinesSection';
import { FleetRoles, showRoles } from '../components/FleetRoles';
import { FleetRolloutModal } from '../components/FleetRolloutModal';
import { MachineDrainDialog } from '../components/MachineDrainDialog';
import { MachinePanel } from '../components/MachinePanel';
import { MachineRemovalNotices } from '../components/MachineRemovalNotices';
import { Toast } from '../components/Toast';
import { WorkInProgressSummary } from '../components/WorkInProgressSummary';
import { t } from '../i18n';
import { type SeatInfo, useEntitlements } from '../lib/billing';
import { useTaskQueue } from '../lib/fleet';
import { type FleetTiles, fleetTiles } from '../lib/fleet-attention';
import { MACHINE_PARAM } from '../lib/fleet-machine-panel';
import { filterPatch } from '../lib/fleet-machines';
import { useFleetView } from '../lib/fleet-view';
import { type FleetUtilization as UtilRead, useFleetUtilization } from '../lib/fleetUtilization';
import { useKeyCapacityRead } from '../lib/key-capacity';
import { createMachineDrain } from '../lib/machine-drain';
import {
  createMachinePanel,
  machineOpeners,
  type MachinePanel as Opener,
} from '../lib/machine-panel';
import { createMachineRemovals, type MachineRemovals } from '../lib/machine-removals';
import { canAdminWrite, roleSettled } from '../lib/roles';
import { type MeScopesRead, scopeVerdict } from '../lib/scope-gate';
import { createSendWorkPage } from '../lib/send-work-page';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { createToast } from '../lib/toast';

// Fleet overview (#59). Container: the header with the plan line, the four health tiles
// and Needs attention (#6559), Fleet Live (#6778, its own read and stream topic), the
// Machines list (#6560), then Work in progress beside
// AI accounts, and AI roles and models last (#6788). Work in progress is a read-only
// HEAD of the queue; the full board, with New task, Stop and the Details drawer, is
// /fleet/work behind "See all work".
//
// ONE READ FOR THE MACHINES (owner ruling G3): the tiles, Needs attention, the Machines
// list and the details panel all read `GET /v1/fleet/utilization`, re-read live on the
// account stream's `machines` + `tasks` topics (#7612). The
// page reads no roster (`GET /v1/runners`); only the details panel point-reads its one
// machine, for Instructions and Who it works for (#6563).
//
// THE LIST'S VIEW LIVES IN THE URL (owner ruling G10): `filter`, `q`, `sort`, `dir`,
// `group` and `view`, parsed by `parseFleetView`, and the List view's `page` (#6561),
// parsed by `parsePage`, so a malformed value reads as the default. The tiles write the
// same `filter` the chips do. So does the open machine: `?machine=<id>` (#6563), written
// only by the one opener (`createMachinePanel`), which every card, row, square, Needs
// attention row and menu item calls through `machineOpeners`.
//
// Every write re-reads the utilization query: a drain or undrain flips a status, a
// removal drops a machine, a connect adds one. `token` is injectable for tests (mirrors
// Home / Repos); the router mounts this with no props -> dev session.
//
// WORK WIRING: the summary's rows are page one of `GET /v1/tasks`, the same live read
// /fleet/work's board makes (`useTaskQueue`): a task moving re-reads it. Its Working
// and Waiting counts are
// the tiles' own (`fleetTiles`: Busy's `working`, Waiting in line's `queued`), so
// the band and the tiles cannot disagree.
//
// SEND WORK (#6770): ONE controller for the page (`createSendWorkPage`): the one open form,
// the sent ids in one signal with a `useTaskDetail` read per id, and the same live task queue
// the summary reads, whose pending rows targeted at a machine read Starting on its card. It
// adds no poll of its own.
//
// THE CONTROLLER IS HANDED OUT BY CREDENTIAL, NOT BY ROLE (`canSendWork`): only a SETTLED
// admin/owner whose `/v1/me` holds `write:runners` AND `write:tasks` — the two scopes
// `POST /v1/runners/:id/work` asks for — gets one. Anyone else gets NO controller, so the
// cards show no Send work row at all, never a row whose handler is absent.

/**
 * "View server" (Needs attention's insight row): group by server, show every machine (no
 * filter, no search that could hide the server's), and bring the list into view.
 */
function viewServer({ view, setView }: ReturnType<typeof useFleetView>): void {
  setView({ ...view(), ...filterPatch('all'), group: 'on' });
  document.querySelector('.fleet-machines')?.scrollIntoView?.({ block: 'start' });
}

/** An org at a box budget of 0 (`isOrgUnfunded`): the named refusal, said before any mint. */
const orgUnfundedOf = (seat: SeatInfo | undefined, kind: string | null | undefined): boolean =>
  seat !== undefined &&
  isOrgUnfunded({ accountKind: kind, budget: seat.entitlements.enrollment_box_budget });

/** The health tiles off one utilization read; `queue` is nullish on an older api (#6708). */
const tilesOf = (u: UtilRead): FleetTiles => fleetTiles(u.boxes, u.lanes, u.queue ?? null);

/**
 * SEND WORK (#6770): the settled CREDENTIAL's scopes decide (`scopeVerdict` over
 * `/v1/me`'s `scopes`), never the role table: both `write:runners` and `write:tasks`, the
 * two the send route asks for. Unsettled is not an answer, so nothing is offered before
 * `/v1/me` has settled for this tenant.
 */
const canSendWorkFor = (me: MeScopesRead, canWrite: boolean): boolean =>
  canWrite &&
  scopeVerdict(me, 'write:runners') === 'allowed' &&
  scopeVerdict(me, 'write:tasks') === 'allowed';

/**
 * The machine details panel, opened through the URL's machine parameter (#6563): the
 * page's ONE opener (`machineOpeners`), which every card, row, square and menu item
 * calls through.
 */
function machinePanelFor(
  params: ReturnType<typeof useSearchParams>[0],
  setParams: ReturnType<typeof useSearchParams>[1],
): ReturnType<typeof createMachinePanel> {
  return createMachinePanel({
    param: () => {
      const id = params[MACHINE_PARAM];
      return typeof id === 'string' ? id : null;
    },
    setParam: (id) => setParams({ [MACHINE_PARAM]: id ?? undefined }, { replace: true }),
  });
}

/**
 * The overlay stack — every dialog the page owns. Split out so the Fleet function
 * itself just decides order; the modals are a static block once state settles.
 * `dismiss` of the success toast rides alongside so every feedback channel has one
 * home here.
 */
function FleetModals(props: {
  readonly token: string | null;
  readonly adding: () => boolean;
  readonly closeAdding: () => void;
  readonly rolling: () => boolean;
  readonly closeRolling: () => void;
  /** The entitlements read for the connect dialog's plan line. */
  readonly seat: () => SeatInfo | undefined;
  /** An org at a limit of 0: the dialog says the named refusal, never "0 of 0". */
  readonly orgUnfunded: () => boolean;
  /** The machine details panel (#6563) and what it reads and writes through. */
  readonly machinePanel: Opener;
  readonly boxes: () => Parameters<typeof MachinePanel>[0]['boxes'];
  readonly canWrite: () => boolean;
  /** `/v1/me`'s `is_platform_admin`: the update dialog's promote section (#6735). */
  readonly staff: () => boolean;
  readonly removals: MachineRemovals;
  readonly onViewServer: () => void;
  readonly reread: () => void;
  readonly drain: ReturnType<typeof createMachineDrain>;
  readonly toast: ReturnType<typeof createToast>;
}): JSX.Element {
  return (
    <>
      <ConnectMachineModal
        open={props.adding()}
        token={props.token}
        seat={props.seat()}
        orgUnfunded={props.orgUnfunded()}
        onClose={props.closeAdding}
        onMinted={props.reread}
      />
      <FleetRolloutModal
        open={props.rolling()}
        token={props.token}
        staff={props.staff()}
        onClose={props.closeRolling}
        onStarted={props.reread}
      />
      <MachinePanel
        panel={props.machinePanel}
        boxes={props.boxes()}
        canWrite={props.canWrite()}
        token={props.token}
        removals={props.removals}
        onViewServer={props.onViewServer}
        onWritten={props.reread}
        notify={props.toast.notify}
      />
      <MachineDrainDialog
        machine={props.drain.drain.pending()}
        onCancel={props.drain.drain.cancel}
        onConfirm={() => void props.drain.drain.confirm()}
      />
      <Toast
        message={props.toast.toast()?.message ?? null}
        tone={props.toast.toast()?.tone ?? 'success'}
        onDismiss={props.toast.dismiss}
      />
    </>
  );
}

/**
 * Whether any dialog this page owns is open: its whole overlay stack (`FleetModals`), the
 * connect and update dialogs, the machine details panel (whose removal confirms live inside
 * it) and the drain confirm. The floating Core reads THIS, never the DOM for a scrim (#6779).
 */
function anyDialogOpen(
  adding: () => boolean,
  rolling: () => boolean,
  panel: Opener,
  drain: ReturnType<typeof createMachineDrain>,
): boolean {
  return adding() || rolling() || panel.id() !== null || drain.drain.pending() !== null;
}

export function Fleet(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const me = useMe(token);
  // The summary's head of the queue, live on the `tasks` topic (#7612).
  const tasks = useTaskQueue(token);
  // The plan line (#6559): the comparison every connect makes, and, on an org at 0, the
  // named refusal said up front, before any mint.
  const seat = useEntitlements(token);
  const orgUnfunded = (): boolean => orgUnfundedOf(seat.data, me.data?.kind);
  const hasSession = token !== null;
  // Write actions are admin-only (owner ruling G21), on a role that has answered for
  // THIS org (`roleSettled`): a carried-over placeholder must not flash a button.
  const canWrite = (): boolean => roleSettled(me) && canAdminWrite(me.data?.role);
  // SEND WORK (#6770): the settled CREDENTIAL's scopes decide (`canSendWorkFor`), never
  // the role table.
  const canSendWork = (): boolean => canSendWorkFor(me, canWrite());

  const utilization = useFleetUtilization(token);
  // Each AI account's live usage (#6864): #6863's one capacity read, polled at 20 s (no
  // stream topic carries key capacity).
  const capacity = useKeyCapacityRead(token);
  const tiles = createMemo(() => utilization.data && tilesOf(utilization.data));
  // No machine at all: the first-run screen (#6567) instead of an empty list.
  const noMachines = (): boolean => utilization.data?.boxes.length === 0;
  const reread = (): void => {
    void utilization.refetch();
    void seat.refetch();
  };

  const fleetView = useFleetView();
  const { view, setView, page, setPage } = fleetView;

  const [adding, setAdding] = createSignal(false);
  const [rolling, setRolling] = createSignal(false);
  const [params, setParams] = useSearchParams();
  const machinePanel = machinePanelFor(params, setParams);
  const openers = machineOpeners(machinePanel);
  const toastController = createToast();
  const drain = createMachineDrain({
    token,
    onDone: (message) => toastController.notify(message),
    onError: (message) => toastController.notify(message, 'error'),
    onWritten: reread,
  });
  const dialogOpen = (): boolean => anyDialogOpen(adding, rolling, machinePanel, drain);
  const notify = toastController.notify;
  // A removal whose token cascade failed stays a page notice, whatever the panel shows.
  const removals = createMachineRemovals({ token, panel: machinePanel, notify, onWritten: reread });
  const sendWork = createSendWorkPage({ token, queue: tasks.rows, notify });

  return (
    <>
      <FleetHeader
        seat={seat.data}
        seatError={seat.isError}
        orgUnfunded={orgUnfunded()}
        canWrite={hasSession && canWrite()}
        firstRun={hasSession && noMachines()}
        onConnect={() => setAdding(true)}
        onUpdateSoftware={() => setRolling(true)}
        onShowRoles={showRoles}
      />

      <Show when={hasSession} fallback={<EmptyState message={t('fleet.no_session')} />}>
        <Show when={canWrite()}>{<MachineRemovalNotices removals={removals} />}</Show>
        <Show when={!noMachines()}>
          <FleetHealthTiles
            tiles={tiles()}
            error={utilization.isError}
            filter={view().filter}
            onFilter={(filter) => setView({ ...view(), ...filterPatch(filter) })}
          />
          <FleetAttention
            boxes={utilization.data?.boxes ?? []}
            keyRoom={utilization.data?.key_room ?? null}
            onView={openers.onManage}
            onViewServer={() => viewServer(fleetView)}
          />
          {/* Fleet Live (#6778, #6779): its one write, the Merge Gate, rides `canWrite`. */}
          <FleetLive token={token} canWrite={canWrite()} dialogOpen={dialogOpen} notify={notify} />
        </Show>

        <Show
          when={!noMachines()}
          fallback={<FleetFirstRun canWrite={canWrite()} onConnect={() => setAdding(true)} />}
        >
          <FleetMachinesSection
            read={utilization}
            view={view()}
            onView={setView}
            page={page()}
            onPage={setPage}
            canWrite={canWrite()}
            onManage={openers.onManage}
            onRename={openers.onRename}
            onDrain={drain.drain.ask}
            onUndrain={drain.undrain}
            onRemove={openers.onRemove}
            notify={toastController.notify}
            onWritten={reread}
            token={token}
            send={canSendWork() ? sendWork : undefined}
          />
        </Show>

        {/* Work in progress beside AI accounts (the prototype's pair), both off the
            page's one utilization read. */}
        <div class="fleet-duo">
          <WorkInProgressSummary
            rows={tasks.rows}
            working={() => tiles()?.busy.working ?? null}
            waiting={() => tiles()?.queued ?? null}
            machineName={(id) =>
              utilization.data?.boxes.find((b) => b.id === id)?.display_name ?? null
            }
          />
          <Show when={utilization.data?.keys && utilization.data}>
            {(u) => (
              <AiAccountsPanel
                keys={u().keys}
                status={u().keys_status}
                capacity={{ status: capacity.status, data: capacity.data }}
              />
            )}
          </Show>
        </div>

        <FleetRoles token={token} />

        <FleetModals
          token={token}
          adding={adding}
          closeAdding={() => setAdding(false)}
          rolling={rolling}
          closeRolling={() => setRolling(false)}
          seat={() => seat.data}
          orgUnfunded={orgUnfunded}
          machinePanel={machinePanel}
          boxes={() => utilization.data?.boxes}
          canWrite={canWrite}
          staff={() => me.data?.is_platform_admin === true}
          removals={removals}
          onViewServer={() => viewServer(fleetView)}
          reread={reread}
          drain={drain}
          toast={toastController}
        />
      </Show>
    </>
  );
}
